/**
 * Gate.io REST API Client
 * @see https://www.gate.io/docs/developers/apiv4/en/
 */

import type {
  GateCandlesRequest,
  GateCandlesResponse,
  GateCurrencyPairResponse,
  GateOrderbookRequest,
  GateOrderbookResponse,
  GateTickersRequest,
  GateTickersResponse,
  GateTradesRequest,
  GateTradesResponse,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://api.gateio.ws/api/v4';
const LEGACY_BASE_URL = 'https://www.gate.io/apiw/v2';

export const gateRestEndpoints = {
  currencyPair: (pair: string) => `${LEGACY_BASE_URL}/market/currency_pairs/${pair}`,
  orderbook: `${BASE_URL}/spot/order_book`,
  candlesticks: `${BASE_URL}/spot/candlesticks`,
  trades: `${BASE_URL}/spot/trades`,
  tickers: `${BASE_URL}/spot/tickers`,
} as const;

/**
 * Returns precision settings for a specific pair.
 * @see https://www.gate.io/docs/developers/apiv4/en/#retrieve-detail-of-specify-currency-pair
 */
export const getGateCurrencyPair = async (
  pair: string,
  options?: RestRequestOptions,
): Promise<GateCurrencyPairResponse> => {
  const response = await get<GateCurrencyPairResponse>({
    url: gateRestEndpoints.currencyPair(pair),
    options,
  });

  return response.data;
};

/**
 * Get market depth information.
 *
 * Market depth buy orders are sorted by price from high to low, sell orders are reversed.
 *
 * @param params.currency_pair - Currency pair (required)
 * @param params.limit - Number of depth levels
 * @param params.interval - Price precision for depth aggregation, 0 means no aggregation, defaults to 0
 * @param params.with_id - Return order book update ID
 *
 * @see https://www.gate.com/docs/developers/apiv4/en/#get-market-depth-information
 */
export const getGateOrderbook = async (
  params: GateOrderbookRequest,
  options?: RestRequestOptions,
): Promise<GateOrderbookResponse> => {
  const response = await get<GateOrderbookResponse, GateOrderbookRequest>({
    url: gateRestEndpoints.orderbook,
    params,
    options,
  });

  return response.data;
};

/**
 * Market K-line chart (candlesticks).
 *
 * Maximum of 1000 points can be returned in a query. Be sure not to exceed the limit
 * when specifying from, to, and interval.
 *
 * Supported intervals: 1s, 10s, 1m, 5m, 15m, 30m, 1h, 4h, 8h, 1d, 7d, 30d
 * Note: 30d represents a calendar month, not aligned to 30 days.
 *
 * @param params.currency_pair - Currency pair (required)
 * @param params.interval - Time interval between data points
 * @param params.from - Start time in Unix timestamp seconds
 * @param params.to - End time in Unix timestamp seconds
 * @param params.limit - Maximum number of data points (conflicts with from/to)
 *
 * @see https://www.gate.com/docs/developers/apiv4/en/#market-k-line-chart
 */
export const getGateCandles = async (
  params: GateCandlesRequest,
  options?: RestRequestOptions,
): Promise<GateCandlesResponse> => {
  const response = await get<GateCandlesResponse, GateCandlesRequest>({
    url: gateRestEndpoints.candlesticks,
    params,
    options,
  });

  return response.data;
};

/**
 * Query market transaction records.
 *
 * Supports querying by time range using `from` and `to` parameters or pagination based on `last_id`.
 * By default, queries the last 30 days.
 *
 * Pagination based on `last_id` is no longer recommended. If `last_id` is specified, the time range
 * query parameters will be ignored.
 *
 * When using limit & page pagination to retrieve data, the maximum number of pages is 100,000,
 * that is, limit * (page - 1) <= 100,000.
 *
 * @param params.currency_pair - Currency pair (required)
 * @param params.limit - Maximum number of items (default: 100, min: 1, max: 1000)
 * @param params.last_id - ID of last record for pagination
 * @param params.reverse - Whether to retrieve data less than last_id
 * @param params.from - Start timestamp for the query
 * @param params.to - End timestamp for the query
 * @param params.page - Page number
 *
 * @see https://www.gate.com/docs/developers/apiv4/en/#query-market-transaction-records
 */
export const getGateTrades = async (
  params: GateTradesRequest,
  options?: RestRequestOptions,
): Promise<GateTradesResponse> => {
  const response = await get<GateTradesResponse, GateTradesRequest>({
    url: gateRestEndpoints.trades,
    params,
    options,
  });

  return response.data;
};

/**
 * Get currency pair ticker information.
 *
 * If `currency_pair` is specified, only query that currency pair; otherwise return all information.
 *
 * @param params.currency_pair - Currency pair (optional, returns all if not specified)
 * @param params.timezone - Timezone for price change calculation (utc0 | utc8 | all)
 *
 * @see https://www.gate.com/docs/developers/apiv4/en/#get-currency-pair-ticker-information
 */
export const getGateTickers = async (
  params: GateTickersRequest = {},
  options?: RestRequestOptions,
): Promise<GateTickersResponse> => {
  const response = await get<GateTickersResponse, GateTickersRequest>({
    url: gateRestEndpoints.tickers,
    params,
    options,
  });

  return response.data;
};
